import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert, Spinner, Image } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/transitions.css'; // We'll create this file next

// Logo styling
const logoStyle = {
  height: '30px',
  marginBottom: '20px'
};

// Page container style for centering
const pageContainerStyle = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: '-7vh' // Move content higher on the page
};

// Central loading spinner style
const centralLoadingStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 2000,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.9)'
};

const OpportunitySearch = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  
  const [opportunityId, setOpportunityId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [processingIdParam, setProcessingIdParam] = useState(false);
  
  // Memoize the submit function to prevent recreating it on every render
  const submitOpportunity = useCallback((id) => {
    console.log("[submitOpportunity] Starting with ID:", id);
    
    if (!id || !id.trim()) {
      console.log("[submitOpportunity] No valid ID provided");
      setError('Please enter an opportunity ID');
      setProcessingIdParam(false);
      return;
    }
    
    // Clean the ID (remove any whitespace)
    const cleanId = id.trim();
    console.log("[submitOpportunity] Processing cleaned ID:", cleanId);
    
    setLoading(true);
    setError(null);
    
    // Clear the processing flag after a timeout to prevent infinite loops
    const safetyTimeout = setTimeout(() => {
      console.log("[submitOpportunity] Safety timeout triggered - resetting processing flag");
      setProcessingIdParam(false);
    }, 5000);
    
    // Navigate immediately without delay
    console.log("[submitOpportunity] Navigating to opportunity detail:", cleanId);
    navigate(`/opportunity/${cleanId}`);
    
    // Reset flag after navigation completes
    setTimeout(() => {
      console.log("[submitOpportunity] Resetting processing flag after navigation");
      setProcessingIdParam(false);
      // Clear safety timeout since we've reset the flag
      clearTimeout(safetyTimeout);
    }, 500);
  }, [navigate]);
  
  // Handle URL parameters and auto-submission
  useEffect(() => {
    console.log("[useEffect] Starting with auth state:", isAuthenticated, "processingFlag:", processingIdParam);
    
    // Skip if already processing or auth state is not determined yet
    if (processingIdParam || isAuthenticated === null || isAuthenticated === undefined) {
      console.log("[useEffect] Skipping - already processing or auth not ready");
      return;
    }
    
    // Create a flag to track if this effect instance is still current
    let isMounted = true;
    
    // Parse URL parameters
    const searchParams = new URLSearchParams(location.search);
    const idParam = searchParams.get('id');
    
    console.log("[useEffect] URL param check - ID param:", idParam, "Auth state:", isAuthenticated);
    
    // Process the opportunity ID
    const processOpportunityId = async () => {
      if (!isMounted) return;
      
      // Case 1: ID in URL and user is authenticated - process immediately
      if (idParam && isAuthenticated) {
        console.log("[processOpportunityId] ID in URL & authenticated - auto-submitting");
        setProcessingIdParam(true);
        setOpportunityId(idParam);
        
        // Clear the URL parameter to prevent reprocessing on page refresh
        window.history.replaceState({}, document.title, '/');
        
        // Auto-submit
        submitOpportunity(idParam);
      } 
      // Case 2: ID in URL but user not authenticated - store & redirect to login
      else if (idParam && !isAuthenticated) {
        console.log("[processOpportunityId] ID in URL but not authenticated - storing & redirecting");
        localStorage.setItem('pendingOpportunityId', idParam);
        localStorage.setItem('redirectUrl', window.location.href);
        
        // Navigate to login (auth system will redirect back after login)
        navigate('/login');
      } 
      // Case 3: No ID in URL but authenticated with stored ID - process stored ID
      else if (!idParam && isAuthenticated) {
        const storedId = localStorage.getItem('pendingOpportunityId');
        
        if (storedId) {
          console.log("[processOpportunityId] No ID in URL but found stored ID:", storedId);
          setProcessingIdParam(true);
          setOpportunityId(storedId);
          
          // Clear stored values
          localStorage.removeItem('pendingOpportunityId');
          localStorage.removeItem('redirectUrl');
          
          // Auto-submit
          submitOpportunity(storedId);
        } else {
          console.log("[processOpportunityId] No ID in URL or storage - nothing to auto-process");
        }
      }
    };
    
    // Start processing with a small delay to ensure state is stable
    const timer = setTimeout(processOpportunityId, 100);
    
    // Animation timing
    const animationTimer = setTimeout(() => {
      if (isMounted) {
        setPageLoaded(true);
      }
    }, 100);
    
    // Cleanup function
    return () => {
      clearTimeout(timer);
      clearTimeout(animationTimer);
      isMounted = false;
      console.log("[useEffect] Cleanup triggered");
    };
  }, [location, isAuthenticated, navigate, submitOpportunity, processingIdParam]);
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("[handleSubmit] Manual form submission with ID:", opportunityId);
    submitOpportunity(opportunityId);
  };
  
  return (
    <div className={`fade-in ${pageLoaded ? 'active' : ''}`} style={pageContainerStyle}>
      {/* Single centered loading spinner */}
      {/* {loading && (
        <div style={centralLoadingStyle} className="single-spinner-container">
          <Spinner
            animation="border"
            role="status"
            variant="primary"
            style={{ 
              width: '3rem', 
              height: '3rem', 
              border: '0.25em solid rgba(46, 62, 159, 0.2)',
              borderRightColor: 'var(--primary)'
            }}
          />
          <p className="mt-3 fs-5">Loading opportunity data...</p>
        </div>
      )} */}
      
      <Container>
        <Row className="justify-content-center">
          <Col md={8} lg={6} className="text-center">
            
            <Card className="shadow-lg border-0 slide-in-right">
              
              <Card.Body className="p-4">
                {error && (
                  <Alert variant="danger" className="slide-in-down">
                    {error}
                  </Alert>
                )}
                
                <Form onSubmit={handleSubmit} className="fade-in">
                  {/* <Image 
                    src="/BBFLogo.svg"  
                    alt="Company Logo" 
                    style={logoStyle} 
                    className="mx-auto"
                  /> */}
                  <Form.Group className="mb-4 text-start">
                    <Form.Label>Salesforce Opportunity ID</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter opportunity ID"
                      value={opportunityId}
                      onChange={(e) => setOpportunityId(e.target.value)}
                      disabled={loading}
                      className="form-control-lg"
                    />
                    
                  </Form.Group>
                  
                  <div className="d-grid mt-4">
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={loading}
                      className="d-flex justify-content-center align-items-center py-2"
                      size="lg"
                    >
                      <span className={loading ? "invisible" : "pulse-on-hover"}>Search Opportunity</span>
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default OpportunitySearch; 